import axios, { AxiosInstance } from "axios";
import Vue from "vue";
import router from "@router/";
import store from "@store/";
import { Route } from "vue-router";

export interface CustomAxiosInstance extends AxiosInstance {
    getAccessToken: () => Promise<void | Route>;
}

export async function createInstance(baseURL): Promise<CustomAxiosInstance> {
    const instance = axios.create({
        baseURL: baseURL,
        withCredentials: true,
    }) as CustomAxiosInstance;

    function getAccessToken() {
        return instance
            .put("/auth")
            .then((response) => {
                store.commit("auth/setAccessToken", response.data.access_token);
            })
            .catch(() => router.push("/login"));
    }

    function getCredentials() {
        return instance
            .get("/credentials")
            .then((response) => store.commit("auth/setCredentials", response.data))
            .catch(() => console.error("error loading credentials"));
    }

    function requestInterceptor(config) {
        if (store.state.auth.access_token) config.headers["Authorization"] = `Bearer ${store.state.auth.access_token}`;
        return config;
    }

    function responseInterceptor(error) {
        const request = error.config;
        const response = error.response;

        //if no auth error
        if (response.status !== 401 && response.status !== 403) return Promise.reject(error);

        //if no or invalide refresh_token
        if (
            (response.status == 403 && response.data == "invalide refresh_token") ||
            (response.status == 401 && response.data == "no refresh_token")
        ) {
            if (router.currentRoute.name !== "Login") router.push("/login");
            return Promise.reject(error);
        }

        //if no or invalide access_token
        if ((response.status == 403 && response.data == "invalide access_token") || (response.status == 401 && response.data == "no access_token")) {
            request._retry = true;
            return getAccessToken()
                .then(() => {
                    return instance(request);
                })
                .catch((error) => {
                    return Promise.reject(error);
                });
        }
    }

    instance.interceptors.request.use(requestInterceptor, (error) => Promise.reject(error));
    instance.interceptors.response.use((response) => response, responseInterceptor);
    instance.getAccessToken = getAccessToken;

    try {
        await getAccessToken();
        await getCredentials();
    } catch (error) {
        console.warn("credentials failed");
    }

    return instance;
}
